import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Crown } from "lucide-react";
import { useProState } from "../lib/pro";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 15;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh, email } = useProState();
  const [state, setState] = useState("polling"); // polling | success | error | expired
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }

    const poll = async () => {
      if (stopped.current) return;
      attempts.current += 1;
      try {
        const res = await fetch(`${API}/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error("status failed");
        const data = await res.json();
        setInfo(data);

        if (data.payment_status === "paid") {
          stopped.current = true;
          // Refresh Pro entitlement for the customer's email
          await refresh(data.email || email);
          setState("success");
          toast.success("Pro unlocked. Welcome aboard.");
          return;
        }
        if (data.status === "expired") {
          stopped.current = true;
          setState("expired");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          stopped.current = true;
          setState("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        console.error(err);
        if (attempts.current >= MAX_ATTEMPTS) {
          stopped.current = true;
          setState("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => {
      stopped.current = true;
    };
  }, [sessionId, refresh, email]);

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-32" data-testid="checkout-success-page">
      <div className="pop-card p-8 sm:p-10 text-center">
        {state === "polling" && (
          <>
            <span className="inline-grid place-items-center w-16 h-16 rounded-2xl bg-brand-cream-soft text-brand-green">
              <Loader2 className="w-8 h-8 animate-spin" strokeWidth={2} />
            </span>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink mt-5 tracking-tight">
              Confirming your payment…
            </h1>
            <p className="text-sm text-brand-mute mt-2">Hang tight, this usually takes just a few seconds.</p>
          </>
        )}

        {state === "success" && (
          <>
            <span className="inline-grid place-items-center w-16 h-16 rounded-2xl bg-brand-green/10 text-brand-green">
              <CheckCircle2 className="w-8 h-8" strokeWidth={2} />
            </span>
            <div className="inline-flex items-center gap-1.5 pop-pill bg-brand-gold/15 text-brand-gold border border-brand-gold/30 mt-5">
              <Crown className="w-3.5 h-3.5" strokeWidth={2.5} /> Pro active
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink mt-4 tracking-tight" data-testid="checkout-success-heading">
              You're all set.
            </h1>
            <p className="text-sm text-brand-mute mt-2 max-w-sm mx-auto">
              {info?.email && (
                <>Pro is now active for <span className="font-medium text-brand-ink">{info.email}</span>.{" "}</>
              )}
              Unlimited events, post-event reports, comparison, and printable checklist are unlocked.
            </p>
            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              <button onClick={() => navigate("/dashboard")} className="pop-btn-primary" data-testid="success-dashboard-btn">
                Open dashboard <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/planner" className="pop-btn-secondary" data-testid="success-planner-btn">
                Plan an event
              </Link>
            </div>
          </>
        )}

        {(state === "error" || state === "expired") && (
          <>
            <span className="inline-grid place-items-center w-16 h-16 rounded-2xl bg-brand-error/10 text-brand-error">
              <AlertCircle className="w-8 h-8" strokeWidth={2} />
            </span>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink mt-5 tracking-tight">
              {state === "expired" ? "Checkout expired" : "We couldn't confirm payment"}
            </h1>
            <p className="text-sm text-brand-mute mt-2 max-w-sm mx-auto">
              {state === "expired"
                ? "Your Stripe checkout session expired before payment completed. Please try again."
                : "If you completed payment, refresh in a minute — webhooks may still be syncing. Otherwise, please try again."}
            </p>
            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              <Link to="/" className="pop-btn-primary" data-testid="success-retry-btn">Back to home</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
